import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import DeviceHealthAlertDialog from 'components/dialogs/DeviceHealthAlertDialog';
import ScanErrorDialog from 'components/dialogs/ScanErrorDialog';
import Loader from 'components/Loader';
import useMedsiWebMonitor, {
  type MedsiMonitorCompletePayload,
} from 'hooks/medsi/useMedsiWebMonitor';
import { useMedsiRuntimeHealth } from 'hooks/medsi/useMedsiRuntimeHealth';
import usePageVisibility from 'hooks/usePageVisibility';
import { MILESTONES } from 'lib/constants';
import { getMedsiScanConfig } from 'lib/medsi/config';
import { isRuntimeDeviceAbortSource } from 'lib/medsi/device-health';
import { mapUserAttributesToRppgDemographics } from 'lib/medsi/demographics';
import { mapRppgFinalResultToBinahData } from 'lib/medsi/mapRppgToBinahData';
import { mapRppgQualityReasons } from 'lib/medsi/mapRppgQualityReasons';
import { SessionState } from 'lib/medsi/scan-compat';
import { getCurrentTimestamp, isMilestoneCompleted } from 'lib/utils';
import { PATH_ROUTES } from 'routes/path';
import { usePostOnboardingStep } from 'services/dashboard/hooks';
import { patchScanSession, postHealthReading } from 'services/face-scan/api';
import { captureUserActivity } from 'services/profile/api';
import useAuthStore from 'store/auth';
import { USER_ACTIVITY } from '../../../types';
import MedsiScan from './medsi/MedsiScan';

export type ValidityCount = {
  [key: string]: number;
};

export default function MedsiMonitor() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userAttributes = useAuthStore(state => state.userAttributes);

  const videoRef = useRef<HTMLVideoElement>(null);

  const [startMeasuring, setStartMeasuring] = useState(false);
  const [imageValidityJSON, setImageValidityJSON] = useState<ValidityCount>({});
  const [readingId, setReadingId] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [completedPayload, setCompletedPayload] =
    useState<MedsiMonitorCompletePayload | null>(null);
  const submittedReadingIdRef = useRef<string | null>(null);
  const pendingQualityRescanRef = useRef(false);

  const demographics = useMemo(
    () => mapUserAttributesToRppgDemographics(userAttributes),
    [userAttributes],
  );

  const handleValidityJSON = useCallback((validity: string) => {
    setImageValidityJSON(prev => ({
      ...prev,
      [validity]: (prev[validity] || 0) + 1,
    }));
  }, []);

  const onComplete = useCallback((payload: MedsiMonitorCompletePayload) => {
    if (
      !payload.readingId ||
      submittedReadingIdRef.current === payload.readingId
    ) {
      return;
    }

    submittedReadingIdRef.current = payload.readingId;
    setStartMeasuring(false);
    setCompletedPayload(payload);
  }, []);

  const {
    sessionState,
    vitalSigns,
    error,
    info,
    readingId: orchestratorReadingId,
    phase,
    progress,
    captureQuality,
    qualityAbort,
    qualityContinuePrompt,
    stopScan,
    clearQualityAbort,
    clearQualityContinuePrompt,
    continueScanDespiteAbort,
    hasLiveVitals,
  } = useMedsiWebMonitor(
    videoRef,
    startMeasuring,
    onComplete,
    handleValidityJSON,
    demographics,
  );

  const isPageVisible = usePageVisibility();

  const isCameraReady = sessionState === SessionState.ACTIVE;
  const isMeasuring = useCallback(
    () =>
      !isStopping &&
      !qualityAbort &&
      (phase === "initializing" || phase === "scanning"),
    [isStopping, qualityAbort, phase],
  );

  const isScanningNow = isMeasuring();
  const isProcessingPhase = phase === 'processing' || phase === 'done';

  const {
    isHealthy,
    healthAbort,
    clearHealthAbort,
    assertHealthyForStart,
    presentHealthAbort,
  } = useMedsiRuntimeHealth({
    videoRef,
    isScanning: isScanningNow,
    isProcessing: isProcessingPhase,
  });

  const canMeasure = isScanningNow || (isCameraReady && isHealthy && !qualityAbort);

  const isDeviceHealthQualityAbort =
    qualityAbort != null && isRuntimeDeviceAbortSource(qualityAbort.source);

  const qualityAbortMessages = useMemo(() => {
    if (qualityContinuePrompt) {
      return mapRppgQualityReasons(qualityContinuePrompt.reasons);
    }
    if (!qualityAbort || isDeviceHealthQualityAbort) return null;
    return mapRppgQualityReasons(qualityAbort.reasons);
  }, [qualityAbort, qualityContinuePrompt, isDeviceHealthQualityAbort]);

  const deviceAbortMessages = useMemo(() => {
    if (healthAbort?.messages?.length) return healthAbort.messages;
    if (isDeviceHealthQualityAbort && qualityAbort) {
      return mapRppgQualityReasons(qualityAbort.reasons);
    }
    return null;
  }, [healthAbort, isDeviceHealthQualityAbort, qualityAbort]);

  const showResultsLoader =
    isSubmitting ||
    (!qualityAbort &&
      !qualityContinuePrompt &&
      (phase === 'processing' || phase === 'done'));

  const stopForDeviceHealth = useCallback(async () => {
    if (!isScanningNow && !startMeasuring) return;

    setIsStopping(true);
    setStartMeasuring(false);
    try {
      await stopScan();
    } catch (err) {
      console.error('[MedsiMonitor] device_health stopScan failed:', err);
    } finally {
      setIsStopping(false);
    }
  }, [isScanningNow, startMeasuring, stopScan]);

  // Abort an in-progress scan when runtime health fails.
  useEffect(() => {
    if (!healthAbort || !isScanningNow) return;
    void stopForDeviceHealth();
  }, [healthAbort, isScanningNow, stopForDeviceHealth]);

  // Orchestrator quality abort (abort_recommended streak / vital_predict rescan).
  useEffect(() => {
    if (!qualityAbort) return;
    setStartMeasuring(false);
    setIsStopping(false);
    void stopScan().catch(err => {
      console.error("[MedsiMonitor] quality_abort stopScan failed:", err);
    });
  }, [qualityAbort, stopScan]);

  const { mutateAsync: updateOnboarding } = usePostOnboardingStep();
  const { mutate, data: postHealthRes } = useMutation({
    mutationFn: postHealthReading,
    onSuccess: async (data: any) => {
      if (!data.success) {
        try {
          await captureUserActivity(USER_ACTIVITY.SCAN_ERROR, {
            message: imageValidityJSON,
            reading_id: readingId,
          });
          await patchScanSession({
            session_id: readingId,
            status: USER_ACTIVITY.SCAN_ERROR,
          });
        } catch (err) {
          console.error(err);
        }
        setShowErrorDialog(true);
        setIsSubmitting(false);
        return;
      }

      try {
        if (!isMilestoneCompleted(MILESTONES.QUESTIONNAIRE)) {
          await updateOnboarding({ milestone: MILESTONES.QUESTIONNAIRE });
        }
        if (
          !isMilestoneCompleted(MILESTONES.ONBOARDED) &&
          isMilestoneCompleted(MILESTONES.FIRST_HEALTH_MEASUREMENT)
        ) {
          await updateOnboarding({ milestone: MILESTONES.ONBOARDED });
        }

        captureUserActivity(USER_ACTIVITY.END_SCAN, {
          reading_id: readingId,
        });
        await patchScanSession({
          session_id: readingId,
          status: USER_ACTIVITY.END_SCAN,
        });
      } catch (err) {
        console.error(err);
      }
      setIsSubmitting(false);
      navigate(PATH_ROUTES.FACE_SCAN_RESULT);
    },
    onError: () => {
      submittedReadingIdRef.current = null;
      setIsSubmitting(false);
    },
    onSettled: () => setIsSubmitting(false),
  });

  useEffect(() => {
    if (!completedPayload) return;

    const { finalResult, readingId: completedReadingId, imageValidityCounts } =
      completedPayload;
    setReadingId(completedReadingId);
    setIsSubmitting(true);

    const scanData = mapRppgFinalResultToBinahData(
      finalResult as unknown as Record<string, unknown>,
    );
    const validityErrors =
      Object.keys(imageValidityCounts).length > 0
        ? imageValidityCounts
        : imageValidityJSON;

    mutate({
      data: scanData,
      scan_error: validityErrors,
      timestamp: getCurrentTimestamp(),
      reading_id: completedReadingId,
      binaah_key: getMedsiScanConfig().rppgApiKey,
    });
  }, [completedPayload, imageValidityJSON, mutate]);

  useEffect(() => {
    if (!isStopping) return;
    if (phase === 'idle' && isCameraReady) {
      setIsStopping(false);
    }
  }, [isStopping, phase, isCameraReady]);

  const onMeasure = useCallback(async () => {
    if (isStopping) return;

    if (isMeasuring()) {
      setIsStopping(true);
      setStartMeasuring(false);
      try {
        captureUserActivity(USER_ACTIVITY.STOP_SCAN, {
          message: 'restart session',
          reading_id: readingId,
        });
        await patchScanSession({
          session_id: readingId,
          status: USER_ACTIVITY.STOP_SCAN,
        });
      } catch (err) {
        console.error(err);
      }
      void stopScan().catch(err => {
        console.error('[MedsiMonitor] stopScan failed:', err);
        setIsStopping(false);
      });
      return;
    }

    if (!isCameraReady || !isHealthy) {
      if (!isHealthy) {
        const health = await assertHealthyForStart();
        if (!health.healthy) {
          presentHealthAbort(health.reasons, 'device_health');
        }
      }
      return;
    }

    const health = await assertHealthyForStart();
    if (!health.healthy) {
      presentHealthAbort(health.reasons, 'device_health');
      return;
    }

    const sessionId = orchestratorReadingId ?? readingId;
    if (!sessionId) return;

    try {
      await patchScanSession({
        session_id: sessionId,
        status: USER_ACTIVITY.START_SCAN,
      });
      captureUserActivity(USER_ACTIVITY.START_SCAN, {
        reading_id: sessionId,
      });
    } catch (err) {
      console.error(err);
      return;
    }

    setReadingId(sessionId);
    setStartMeasuring(true);
  }, [
    assertHealthyForStart,
    isCameraReady,
    isHealthy,
    isMeasuring,
    isStopping,
    orchestratorReadingId,
    presentHealthAbort,
    readingId,
    stopScan,
  ]);

  useEffect(() => {
    if (!isPageVisible && isMeasuring() && document.hidden) {
      presentHealthAbort(['CAMERA_INTERRUPTED'], 'camera_interrupt');
      setIsStopping(true);
      setStartMeasuring(false);
      void stopScan().catch(err => {
        console.error('[MedsiMonitor] stopScan failed:', err);
        setIsStopping(false);
      });
    }
  }, [isPageVisible, isMeasuring, presentHealthAbort, stopScan]);

  const onHealthDismiss = useCallback(() => {
    clearHealthAbort();
    if (isDeviceHealthQualityAbort) {
      clearQualityAbort();
    }
  }, [clearHealthAbort, clearQualityAbort, isDeviceHealthQualityAbort]);

  const stopForQualityPrompt = useCallback(async () => {
    setIsStopping(true);
    setStartMeasuring(false);
    clearQualityContinuePrompt();
    try {
      await stopScan();
    } catch (err) {
      console.error('[MedsiMonitor] quality_prompt stopScan failed:', err);
    } finally {
      setIsStopping(false);
    }
  }, [clearQualityContinuePrompt, stopScan]);

  const onQualityAbortDismiss = useCallback(() => {
    if (qualityContinuePrompt) {
      void stopForQualityPrompt();
      return;
    }
    clearQualityAbort();
  }, [clearQualityAbort, qualityContinuePrompt, stopForQualityPrompt]);

  const onQualityAbortRescan = useCallback(() => {
    pendingQualityRescanRef.current = true;
    if (qualityContinuePrompt) {
      void stopForQualityPrompt();
      return;
    }
    clearQualityAbort();
  }, [clearQualityAbort, qualityContinuePrompt, stopForQualityPrompt]);

  const onQualityContinue = useCallback(() => {
    continueScanDespiteAbort();
  }, [continueScanDespiteAbort]);

  // Auto-start after quality abort → Rescan.
  useEffect(() => {
    if (!pendingQualityRescanRef.current) return;
    if (qualityAbort || qualityContinuePrompt || isStopping) return;
    if (!canMeasure || isScanningNow) return;
    pendingQualityRescanRef.current = false;
    void onMeasure();
  }, [
    canMeasure,
    isScanningNow,
    isStopping,
    onMeasure,
    qualityAbort,
    qualityContinuePrompt,
  ]);

  useEffect(() => {
    if (error?.code === -1 || !error?.message) return;
    setStartMeasuring(false);
    setIsStopping(false);
  }, [error]);

  useEffect(() => {
    return () => navigate(0);
  }, []);

  const onErrorClose = () => {
    setShowErrorDialog(false);
    navigate(0);
    navigate(`${PATH_ROUTES.FACE_SCAN}?showScan=true`);
  };

  const onRescan = () => {
    setShowErrorDialog(false);
    onMeasure();
  };

  return (
    <>
      <div className='relative h-[calc(100svh-4rem)] max-h-[calc(100svh-4rem)] overflow-hidden'>
        {showResultsLoader && (
          <div className='fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-white'>
            <Loader className='h-auto' />
            <p className='font-medium text-center text-muted-foreground'>
              {t('medsi_processing_results')}
            </p>
          </div>
        )}
        <MedsiScan
          info={info}
          isCameraReady={isCameraReady}
          canMeasure={canMeasure}
          isMeasuring={isMeasuring}
          isStopping={isStopping}
          onMeasure={onMeasure}
          videoRef={videoRef}
          vitalSigns={vitalSigns}
          scanProgress={progress}
          captureQuality={captureQuality}
          showLiveVitals={hasLiveVitals}
          scanError={error?.code !== -1 ? error.message : undefined}
        />
      </div>

      <DeviceHealthAlertDialog
        open={Boolean(deviceAbortMessages?.length)}
        messages={deviceAbortMessages ?? []}
        onDismiss={onHealthDismiss}
      />

      <DeviceHealthAlertDialog
        open={Boolean(qualityAbortMessages?.length)}
        messages={qualityAbortMessages ?? []}
        onDismiss={onQualityAbortDismiss}
        onRescan={onQualityAbortRescan}
        onContinue={qualityContinuePrompt ? onQualityContinue : undefined}
      />

      <ScanErrorDialog
        open={showErrorDialog}
        onClose={onErrorClose}
        onRescan={onRescan}
        error={postHealthRes}
      />
    </>
  );
}
